import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CheckCircle, ArrowRight, Crown, Star, Loader2 } from 'lucide-react';
import { careerAPI, paymentAPI } from '../utils/api';
import authService from '../firebase/authService';

const PaymentSuccessPage = ({ userProfile, selectedCareerPath, onRoadmapGenerated }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isGeneratingRoadmap, setIsGeneratingRoadmap] = useState(false);
  const [isProcessingSubscription, setIsProcessingSubscription] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [isWaitingForAuth, setIsWaitingForAuth] = useState(true);
  const [user, setUser] = useState(null);

  const orderId = searchParams.get('order_id');
  const amount = searchParams.get('amount');
  const tier = searchParams.get('tier');
  const billingCycle = searchParams.get('billing_cycle');

  // Wait for Firebase auth to be ready after redirect
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((currentUser) => {
      setUser(currentUser);
      setIsWaitingForAuth(false);
    });

    // Fallback timeout if auth doesn't respond in 5 seconds
    const timeout = setTimeout(() => {
      setIsWaitingForAuth(false);
    }, 5000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // Process subscription once auth is ready
  useEffect(() => {
    if (isWaitingForAuth) return; // Wait for auth to be ready

    if (orderId) {
      // Check if this is a subscription payment or roadmap purchase
      if (tier && tier !== 'free') {
        processSubscriptionUpgrade();
      } else if (userProfile && selectedCareerPath) {
        generateRoadmap();
      }
    }
  }, [orderId, tier, userProfile, selectedCareerPath, isWaitingForAuth]);

  const processSubscriptionUpgrade = async () => {
    setIsProcessingSubscription(true);

    try {
      // Check if user is authenticated - use state which waits for auth
      const currentUser = user || authService.getCurrentUser();
      if (!currentUser) {
        toast.error('Please sign in to activate subscription');
        // Store the pending order so we can resume after sign in
        localStorage.setItem('pending_subscription', JSON.stringify({
          order_id: orderId,
          tier: tier,
          billing_cycle: billingCycle
        }));
        navigate('/');
        return;
      }

      // Verify payment with Cashfree before activating
      try {
        const verifyRes = await paymentAPI.verifyPayment(orderId);
        // Ensure backend confirmed the payment
        if (!verifyRes || !verifyRes.success) {
          throw new Error('Payment status is not successful or was cancelled.');
        }
      } catch (err) {
        console.error('Payment verification failed:', err);
        throw new Error(err.message || 'Payment verification failed or was cancelled');
      }

      // Call backend API to activate subscription
      const response = await paymentAPI.activateSubscription(orderId, tier);

      if (response.success) {
        setSubscriptionTier(tier);

        // Show success message based on tier
        const tierNames = {
          'starter': 'Starter',
          'professional': 'Professional',
          'enterprise': 'Enterprise'
        };

        toast.success(`${tierNames[tier]} subscription activated successfully!`);

        // Clear pending order from localStorage
        localStorage.removeItem('pending_order');

        // Navigate to premium dashboard after a delay
        setTimeout(() => {
          navigate('/premium-dashboard');
        }, 2000);
      } else {
        throw new Error('Subscription activation failed');
      }

    } catch (error) {
      console.error('Error processing subscription:', error);
      toast.error(error.message || 'Failed to activate subscription. Please contact support.');

      // Redirect to subscription page to retry
      setTimeout(() => {
        navigate('/subscribe');
      }, 3000);
    } finally {
      setIsProcessingSubscription(false);
    }
  };

  const generateRoadmap = async () => {
    setIsGeneratingRoadmap(true);

    try {
      // Verify payment with Cashfree before generating
      try {
        const verifyRes = await paymentAPI.verifyPayment(orderId);
        if (!verifyRes || !verifyRes.success) {
          throw new Error('Payment status is not successful or was cancelled.');
        }
      } catch (err) {
        console.error('Payment verification failed:', err);
        throw new Error(err.message || 'Payment verification failed or was cancelled');
      }

      const roadmapData = await careerAPI.generateRoadmap(
        userProfile,
        selectedCareerPath.title,
        orderId,
        12 // 12 months timeline
      );

      // Store roadmap data in localStorage for immediate access
      localStorage.setItem('roadmapData', JSON.stringify(roadmapData));

      // Store roadmap ID for progress tracking
      if (roadmapData.roadmap_id) {
        localStorage.setItem('selectedRoadmapId', roadmapData.roadmap_id);
      }

      onRoadmapGenerated(roadmapData);
      toast.success('Roadmap generated successfully!');

      // Navigate to roadmap page after a short delay
      setTimeout(() => {
        navigate('/roadmap');
      }, 2000);

    } catch (error) {
      console.error('Error generating roadmap:', error);
      toast.error('Failed to generate roadmap. Please try again.');
    } finally {
      setIsGeneratingRoadmap(false);
    }
  };

  const getSubscriptionIcon = (tier) => {
    switch (tier) {
      case 'starter': return <Star className="w-8 h-8 text-emerald-400" />;
      case 'professional': return <Crown className="w-8 h-8 text-yellow-400" />;
      case 'enterprise': return <Crown className="w-8 h-8 text-purple-400" />;
      default: return <CheckCircle className="w-8 h-8 text-green-600" />;
    }
  };

  const getTierName = (tier) => {
    const tierNames = {
      'starter': 'Starter',
      'professional': 'Professional',
      'enterprise': 'Enterprise'
    };
    return tierNames[tier] || 'Premium';
  };

  // Auto-redirect countdown
  const [countdown, setCountdown] = useState(5);
  const [shouldCountdown, setShouldCountdown] = useState(false);

  useEffect(() => {
    // Start countdown only when processing is complete and we have a result
    if (!isProcessingSubscription && !isGeneratingRoadmap && !isWaitingForAuth && (subscriptionTier || orderId)) {
      setShouldCountdown(true);
    }
  }, [isProcessingSubscription, isGeneratingRoadmap, isWaitingForAuth, subscriptionTier, orderId]);

  useEffect(() => {
    if (!shouldCountdown) return;
    if (countdown <= 0) {
      navigate(subscriptionTier ? '/premium-dashboard' : '/roadmap');
      return;
    }
    const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, shouldCountdown, navigate, subscriptionTier]);

  // Show loading while waiting for Firebase auth to initialize
  if (isWaitingForAuth) {
    return (
      <div className="min-h-screen bg-[#050505] py-12 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-white font-mono text-sm">Verifying your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex items-center justify-center py-16">
      {/* Background glow effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-1/3 w-[300px] h-[300px] bg-teal-500/8 rounded-full blur-[80px]" />
      </div>

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />

      <div className="relative z-10 max-w-lg mx-auto px-4 w-full">
        {/* Success Icon with animated glow ring */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center animate-pulse-glow">
              {subscriptionTier ? (
                getSubscriptionIcon(subscriptionTier)
              ) : (
                <CheckCircle className="w-12 h-12 text-emerald-400" />
              )}
            </div>
            {/* Outer ring animation */}
            <div className="absolute -inset-3 border border-emerald-500/20 rounded-full animate-[pulse-ring_2s_infinite]" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-white text-center font-mono tracking-tight mb-3">
          {subscriptionTier ? 'SUBSCRIPTION ACTIVATED' : 'PAYMENT SUCCESSFUL'}
        </h1>
        <p className="text-slate-400 text-center mb-8 font-mono text-sm">
          {subscriptionTier
            ? `Welcome to ${getTierName(subscriptionTier)}! Your subscription is now active.`
            : 'Thank you for your payment. Your career roadmap is being generated...'
          }
        </p>

        {/* Receipt Card */}
        {orderId && (
          <div className="bg-white/5 border border-white/10 p-6 mb-8 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Transaction Receipt</span>
              <div className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-[10px] font-mono text-emerald-400 uppercase">Confirmed</span>
              </div>
            </div>

            <div className="space-y-3 border-t border-white/10 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-xs font-mono text-slate-500">ORDER_ID</span>
                <span className="text-xs font-mono text-white">{orderId}</span>
              </div>
              {amount && (
                <div className="flex justify-between items-center">
                  <span className="text-xs font-mono text-slate-500">AMOUNT</span>
                  <span className="text-xs font-mono text-emerald-400 font-bold">${amount}</span>
                </div>
              )}
              {subscriptionTier && (
                <div className="flex justify-between items-center">
                  <span className="text-xs font-mono text-slate-500">PLAN</span>
                  <span className="text-xs font-mono text-white">
                    {getTierName(subscriptionTier)} {billingCycle === 'yearly' ? '(Annual)' : '(Monthly)'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Processing State or CTA */}
        {isProcessingSubscription || isGeneratingRoadmap ? (
          <div className="text-center mb-6">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-400 font-mono text-sm">
              {isProcessingSubscription
                ? 'Activating your subscription...'
                : 'Generating your personalized career roadmap...'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Auto-redirect countdown */}
            <div className="text-center mb-4">
              <p className="text-slate-500 text-xs font-mono">
                Redirecting in <span className="text-emerald-400 font-bold tabular-nums">{countdown}s</span>...
              </p>
              {/* Countdown bar */}
              <div className="w-48 h-0.5 bg-white/10 mx-auto mt-2 overflow-hidden rounded-full">
                <div
                  className="h-full bg-emerald-500 transition-all duration-1000 ease-linear"
                  style={{ width: `${(countdown / 5) * 100}%` }}
                />
              </div>
            </div>

            <button
              onClick={() => navigate(subscriptionTier ? '/premium-dashboard' : '/roadmap')}
              className="w-full flex items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-400 text-black py-3.5 px-6 font-bold font-mono text-sm uppercase tracking-wider transition-all hover:scale-[1.02]"
            >
              <span>{subscriptionTier ? 'Go to Premium Dashboard' : 'View Your Roadmap'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage; 